import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DefaultNotificationsRepoService } from './default-notifications-repo.service';
import { IDefaultNotification, IMessage, INotificationSplit, NotificationType } from '@settings/models';
import { splitTime } from '@settings/helpers';

@Injectable({
  providedIn: 'root'
})
export class DefaultNotificationsService {
  constructor(private _defaultNotificationsRepoService: DefaultNotificationsRepoService) {}

  getDefaultNotifications(): Observable<{ notifications: INotificationSplit[]; messages: IMessage[] }> {
    return this._defaultNotificationsRepoService
      .getDefaultNotifications()
      .pipe(map((defaultNotifications) => this._splitDefaultNotifications(defaultNotifications)));
  }

  patchDefaultNotifications(
    defaultNotifications: INotificationSplit[],
    messages: IMessage[],
    types: NotificationType[]
  ): Observable<undefined> {
    return this._defaultNotificationsRepoService.patchDefaultNotifications(
      this._joinDefaultNotifications(defaultNotifications, messages),
      types
    );
  }

  private _splitDefaultNotifications(defaultNotifications: IDefaultNotification[]): {
    notifications: INotificationSplit[];
    messages: IMessage[];
  } {
    const firstDeadlineNotification = defaultNotifications.find(({ type }) => type === NotificationType.DEADLINE);
    const firstOverviewNotification = defaultNotifications.find(({ type }) => type === NotificationType.OVERVIEW);

    const notifications = defaultNotifications.map((defaultNotification) => {
      const daysOffset = Math.abs(defaultNotification.defaultNotificationDaysOffset);
      return {
        id: defaultNotification.defaultNotificationId,
        type: defaultNotification.type,
        ...splitTime(defaultNotification.time),
        daysOffset
      };
    });

    const messages: IMessage[] = [];

    if (firstDeadlineNotification) {
      const { notificationSubject, notificationMessage, notificationSubjectDefault, notificationMessageDefault } =
        firstDeadlineNotification;
      const isMessageDefault = notificationSubjectDefault && notificationMessageDefault;
      messages.push({
        subject: notificationSubject,
        text: notificationMessage,
        notificationType: NotificationType.DEADLINE,
        isMessageDefault
      });
    } else {
      messages.push({ subject: '', text: '', isMessageDefault: false, notificationType: NotificationType.DEADLINE });
    }
    if (firstOverviewNotification) {
      const { notificationSubject, notificationMessage, notificationSubjectDefault, notificationMessageDefault } =
        firstOverviewNotification;
      const isMessageDefault = notificationSubjectDefault && notificationMessageDefault;
      messages.push({
        subject: notificationSubject,
        text: notificationMessage,
        notificationType: NotificationType.OVERVIEW,
        isMessageDefault
      });
    } else {
      messages.push({ subject: '', text: '', isMessageDefault: false, notificationType: NotificationType.OVERVIEW });
    }

    return { notifications, messages };
  }

  private _joinDefaultNotifications(notifications: INotificationSplit[], messages: IMessage[]): IDefaultNotification[] {
    return notifications.map(({ id, hour, minute, period, daysOffset, type }) => {
      const { subject, text } = messages.find(({ notificationType }) => notificationType === type);
      const time = `${hour}:${minute} ${period}`;
      daysOffset = -Math.abs(daysOffset);
      return {
        time,
        type,
        defaultNotificationId: id,
        defaultNotificationDaysOffset: daysOffset,
        notificationSubject: subject,
        notificationMessage: text
      };
    });
  }
}
